import React, { FC, ReactElement, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Header, Container } from './CategoriesAndTopicsResults.styles';
import PageSection from '../../HOC/PageSection/PageSection';
import { useFilters } from '../../hooks/useFilters';
import { useLocalSort } from '../../hooks/useLocalSort';
import { ICategoryResult } from '../../services/reports';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import SectionTabs from '../SectionTabs/SectionTabs';
import CategoriesAndTopicsTableHeader from './CategoriesAndTopicsTableHeader/CategoriesAndTopicsTableHeader';
import CategoriesAndTopicsTableTile from './CategoriesAndTopicsTableTile/CategoriesAndTopicsTableTile';

interface ICategoriesAndTopicsResults {
  items: ICategoryResult[];
  sectionTitle: ReactElement;
  isExecutiveReport: boolean;
  description: string;
}

const reportKey = 'surveyReport';

const CategoriesAndTopicsResults: FC<ICategoriesAndTopicsResults> = ({
  items,
  sectionTitle,
  isExecutiveReport,
  description,
}) => {
  const { t } = useTranslation();
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { sortItems, loading } = useLocalSort([...items], reportKey, 'categorySort');

  const getCategoriesFilters = useMemo(() => {
    const categories = currentFilters?.surveyReport;

    if (categories) {
      // @ts-ignore
      const catsFilters = JSON.parse(categories);

      return {
        legend: catsFilters?.legend || false,
        mode: catsFilters?.mode || 'totals',
      };
    }
  }, [currentFilters?.surveyReport]);

  const activeMode = getCategoriesFilters?.mode || 'totals';

  const isTotalsTabActive = useMemo(() => activeMode === 'totals', [activeMode]);

  const sectionTabs = useMemo(
    () => [
      { name: t('report.general.totals'), value: 'totals' },
      { name: t('report.general.teams'), value: 'teams' },
    ],
    [t]
  );

  const renderTable = useMemo(() => {
    return sortItems?.map((item: ICategoryResult) => (
      <CategoriesAndTopicsTableTile
        item={item}
        key={item?.category?.code}
        isTotalsTabActive={isTotalsTabActive}
      />
    ));
  }, [sortItems, items, loading, isTotalsTabActive]);

  return (
    <>
      <Header>
        {sectionTitle}
        {isExecutiveReport && (
          <SectionTabs
            activeTab={activeMode}
            setTab={(tab: string) => applyAndMergeFilters(reportKey, 'mode', tab)}
            tabs={sectionTabs}
          />
        )}
      </Header>
      <PageSection>
        <Container>
          <ReportResultsLegend
            noBorder
            description={description}
            legendExpanded={getCategoriesFilters?.legend}
            expandLegend={(status: boolean) => applyAndMergeFilters(reportKey, 'legend', status)}
          />
          <CategoriesAndTopicsTableHeader isTotalsTabActive={isTotalsTabActive} />
          {loading ? <div>loading</div> : renderTable}
        </Container>
      </PageSection>
    </>
  );
};

export default CategoriesAndTopicsResults;
